import { useEffect, useMemo, useState, type FormEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import type {
  Customer360ActivityCategory,
  Customer360ActivityItem,
  Customer360ActivityType,
  Customer360Alert,
  Customer360Overview,
  Customer360QuickAction,
  Customer360QuickActionId,
  Customer360SourceLabel,
  Customer360SummaryCard,
} from "@cream/shared";
import { CUSTOMER_360_ACTIVITY_CATEGORIES, CUSTOMER_360_ACTIVITY_TYPES } from "@cream/shared";
import { Icon, type IconName } from "../components/ui/Icon";
import {
  ActionButton,
  EmptyState,
  ErrorState,
  LoadingState,
  PageHeader,
  SearchInput,
  StatusPill,
} from "../components/operations/OperationalComponents";
import { Modal } from "../components/overlays";
import { creamTokens } from "../design-system/creamDesignSystem";
import { getErr, http } from "../services/apiClient";
import { useCustSrch, useCustomer360, useCustomer360Activity, useCustomer360WriteMutation, useWhatsAppAssignableUsers } from "../hooks/queries";
import { fmt, fmtDateFull } from "../utils/formatters";
import { openExternal } from "../utils/phones";

type CustomerSearchResult = {
  id: string;
  name?: string | null;
  phone?: string | null;
  totalOrderCount?: number | null;
  totalSpendPaise?: number | null;
  profile?: { highValueFlag?: boolean | null } | null;
};

const sourceCopy: Record<Customer360SourceLabel, string> = {
  VERIFIED_CUSTOMER_DATA: "Verified customer data",
  STAFF_ENTERED: "Staff entered",
  ORDER_DERIVED: "Order derived",
  INQUIRY_DERIVED: "Inquiry derived",
  WHATSAPP_DERIVED: "Derived from WhatsApp",
  PAYMENT_DERIVED: "Payment derived",
  SYSTEM_INFERRED: "System inferred",
  LEGACY_PROFILE: "Legacy profile",
  UNAVAILABLE: "Unavailable",
};

const summaryIcons: Record<Customer360SummaryCard["type"], IconName> = {
  OPEN_INQUIRY: "clipboard-list",
  NEXT_OCCASION: "calendar-check",
  OUTSTANDING_AMOUNT: "indian-rupee",
  LAST_ORDER: "shopping-bag",
};

const quickActionIcons: Partial<Record<Customer360QuickAction["id"], IconName>> = {
  CALL: "phone",
  OPEN_WHATSAPP: "message-circle",
  CREATE_INQUIRY: "clipboard-list",
  CREATE_ORDER: "shopping-bag",
  SCHEDULE_FOLLOW_UP: "calendar-check",
  VIEW_OUTSTANDING: "wallet",
  ADD_NOTE: "plus",
  RECORD_OCCASION: "calendar-check",
  ADD_PREFERENCE: "settings",
  ADD_ADDRESS: "map-pin",
  EDIT_CUSTOMER: "user",
  ASSIGN_RM: "users",
};

const activityCategoryLabels: Record<Customer360ActivityCategory, string> = {
  CUSTOMER: "Customer",
  SALES: "Sales",
  FOLLOW_UP: "Follow-ups",
  ORDER: "Orders",
  FINANCE: "Finance",
  WHATSAPP: "WhatsApp",
  MEMORY: "Memory",
  AUDIT: "Audit",
  SYSTEM: "System",
};

const activityIcons: Partial<Record<Customer360ActivityCategory, IconName>> = {
  CUSTOMER: "user",
  SALES: "clipboard-list",
  FOLLOW_UP: "calendar-check",
  ORDER: "shopping-bag",
  FINANCE: "wallet",
  WHATSAPP: "message-circle",
  MEMORY: "brain",
  AUDIT: "shield-check",
  SYSTEM: "gauge",
};

const customer360WriteActions = new Set<Customer360QuickActionId>([
  "EDIT_CUSTOMER",
  "ADD_ADDRESS",
  "RECORD_OCCASION",
  "ADD_PREFERENCE",
  "ADD_NOTE",
  "ASSIGN_RM",
]);

const severityTone: Record<Customer360Alert["severity"], string> = {
  INFO: creamTokens.color.info,
  WARNING: creamTokens.color.warning,
  CRITICAL: creamTokens.color.critical,
};

const displayLabelOverrides: Record<string, string> = {
  ACTIVE: "Active",
  ADVANCE: "Advance payment",
  ASSIGNED: "Assigned",
  BALANCE: "Balance payment",
  BLOCKED: "Blocked",
  CLOSED: "Closed",
  CONVERTED: "Converted",
  DERIVED_FROM_WHATSAPP: "Derived from WhatsApp",
  DORMANT: "Dormant",
  FOLLOWUP_DUE: "Follow-up due",
  FOLLOW_UP_DUE: "Follow-up due",
  FLAVOR: "Flavor",
  HIGH: "High",
  IN_PRODUCTION: "In Production",
  LOST: "Lost",
  LOW: "Low",
  MEDIUM: "Medium",
  NEW: "New",
  NONE: "None",
  OPEN: "Open",
  PARTIAL: "Partial",
  PARTIALLY_PAID: "Partially paid",
  PAID: "Paid",
  RESTRICTED: "Restricted",
  UNKNOWN: "Status unavailable",
  UNASSIGNED: "Unassigned",
  UNAVAILABLE: "Unavailable",
  UPI: "UPI",
  VIP: "VIP",
  WAITING_FOR_CUSTOMER: "Waiting for customer",
  WAITING_FOR_RM: "Waiting for RM",
  WAITING_ON_CUSTOMER: "Waiting on customer",
  WAITING_ON_STAFF: "Waiting on staff",
};

const rawDisplayTokens = new RegExp(`\\b(${Object.keys(displayLabelOverrides).join("|")})\\b`, "g");

const formatMaybeDate = (value?: string | null) => (value ? fmtDateFull(value) : "Not available");

const humanizeDisplayLabel = (value?: string | null, options: { omitUnknown?: boolean } = {}) => {
  if (!value) return "";
  if (options.omitUnknown && value === "UNKNOWN") return "";
  return displayLabelOverrides[value] ?? value.replace(/_/g, " ").toLowerCase().replace(/\b\w/g, (letter) => letter.toUpperCase());
};

const humanizeDisplayText = (value: string) => (
  value
    .replace(/\b(\d+)\s+paise\b/gi, (_match, amount) => fmt(Number(amount)))
    .replace(rawDisplayTokens, (token) => humanizeDisplayLabel(token))
);

const displayValue = (value: string | number | null | undefined) => {
  if (value === null || value === undefined || value === "") return "Not available";
  return typeof value === "string" ? humanizeDisplayText(value) : String(value);
};

const displayList = (items?: string[] | null) => (items?.length ? items.join(", ") : "Not captured");

const actionPath = (action: Customer360QuickAction) => action.route ?? action.target ?? "";

export const handleCustomer360Action = (action: Customer360QuickAction, navigate: (to: string) => void) => {
  if (!action.enabled) return;
  const path = actionPath(action);
  if (!path) return;
  if (path.startsWith("tel:")) {
    openExternal(path);
    return;
  }
  if (path.startsWith("/")) {
    navigate(path);
  }
};

export function Customer360View({
  overview,
  onBack,
  onRefresh,
}: {
  overview: Customer360Overview;
  onBack: () => void;
  onRefresh?: () => void | Promise<unknown>;
}) {
  const navigate = useNavigate();
  const [writeAction, setWriteAction] = useState<Customer360QuickActionId | null>(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<"overview" | "activity">("overview");
  const customer = overview.customer;
  const phone = customer.primaryPhone ?? customer.maskedPhone ?? "Phone restricted";
  const email = customer.email ?? customer.maskedEmail ?? "Email restricted";
  const outstanding = overview.metrics.outstandingPaise?.value;
  const totalSpend = overview.metrics.totalSpendPaise?.value;
  const averageOrder = overview.metrics.averageOrderValuePaise?.value;

  return (
    <section className="cc360-detail" aria-label={`Customer overview for ${customer.displayName}`}>
      <div className="cc360-mobile-back">
        <ActionButton icon="chevron-left" label="Back to results" onClick={onBack} />
      </div>

      <div className="cc360-hero">
        <div className="cc360-avatar" aria-hidden="true">{customer.displayName.slice(0, 1).toUpperCase()}</div>
        <div className="cc360-hero-copy">
          <div className="cc360-eyebrow">Customer 360 · {overview.metadata.contractVersion}</div>
          <h2>{customer.displayName}</h2>
          <div className="cc360-contact">
            <span>{phone}</span>
            <span>{email}</span>
            <span>Since {formatMaybeDate(customer.createdAt)}</span>
          </div>
          <div className="cc360-tags">
            <StatusPill tone="info">{humanizeDisplayLabel(customer.customerType)}</StatusPill>
            <StatusPill tone="success">{humanizeDisplayLabel(customer.lifecycleLabel)}</StatusPill>
            <StatusPill tone="info">{humanizeDisplayLabel(overview.relationship.relationshipOwnerState)}</StatusPill>
          </div>
        </div>
        <div className="cc360-rm-card">
          <span>Relationship Manager</span>
          <strong>{overview.relationship.assignedRm?.name ?? "Unassigned"}</strong>
          <small>{sourceCopy[overview.relationship.relationshipOwnerSource]}</small>
        </div>
      </div>

      <Customer360QuickActions
        actions={overview.quickActions}
        onAction={(action) => {
          if (action.id === "SCHEDULE_FOLLOW_UP") {
            setFollowUpOpen(true);
            return;
          }

          if (customer360WriteActions.has(action.id)) {
            setWriteAction(action.id);
            return;
          }

          handleCustomer360Action(action, navigate);
        }}
      />
      <div className="cc360-tabs" role="tablist" aria-label="Customer 360 sections">
        <button className={activeTab === "overview" ? "is-active" : ""} onClick={() => setActiveTab("overview")} type="button">Overview</button>
        <button className={activeTab === "activity" ? "is-active" : ""} onClick={() => setActiveTab("activity")} type="button">Activity</button>
      </div>

      {activeTab === "overview" ? (
        <Customer360OverviewPanels
          overview={overview}
          totalSpend={totalSpend}
          averageOrder={averageOrder}
          outstanding={outstanding}
        />
      ) : (
        <Customer360ActivityPanel customerId={customer.id} />
      )}

      {writeAction && (
        <Customer360WriteModal
          action={writeAction}
          customer={overview.customer}
          role={overview.metadata.role}
          relationshipOwner={overview.relationship.assignedRm?.id ?? ""}
          onClose={() => setWriteAction(null)}
        />
      )}

      {followUpOpen && (
        <Customer360FollowUpModal
          customer={overview.customer}
          assignedUserId={overview.relationship.assignedRm?.id ?? ""}
          onClose={() => setFollowUpOpen(false)}
          onSaved={onRefresh}
        />
      )}
    </section>
  );
}

function Customer360FollowUpModal({
  customer,
  assignedUserId,
  onClose,
  onSaved,
}: {
  customer: Customer360Overview["customer"];
  assignedUserId?: string;
  onClose: () => void;
  onSaved?: () => void | Promise<unknown>;
}) {
  const [form, setForm] = useState({
    title: `Follow up with ${customer.displayName}`,
    dueAt: "",
    notes: "",
  });

  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const update = (key: string, value: string) => {
    setForm((current) => ({ ...current, [key]: value }));
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSaved(false);

    if (!form.title.trim()) {
      setError("Follow-up title is required.");
      return;
    }

    if (!form.dueAt) {
      setError("Follow-up date and time are required.");
      return;
    }

    const dueAt = new Date(form.dueAt);

    if (Number.isNaN(dueAt.getTime())) {
      setError("Enter a valid follow-up date and time.");
      return;
    }

    try {
      setSaving(true);

      await http.post("/follow-up-tasks", {
        customerId: customer.id,
        title: form.title.trim(),
        notes: form.notes.trim() || undefined,
        dueAt: dueAt.toISOString(),
        assignedToId: assignedUserId || undefined,
      });

      await onSaved?.();
      setSaved(true);
      setTimeout(onClose, 450);
    } catch (err) {
      setError(getErr(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open
      title="Schedule follow-up"
      onClose={onClose}
      footer={
        <>
          <ActionButton icon="x" label="Cancel" onClick={onClose} />
          <ActionButton
            icon="check"
            label={saving ? "Scheduling..." : "Schedule"}
            onClick={() =>
              document.getElementById("cc360-follow-up-submit")?.click()
            }
            tone="primary"
            disabled={saving}
          />
        </>
      }
    >
      <form className="cc360-write-form" onSubmit={submit}>
        <p>Create a governed follow-up task for {customer.displayName}.</p>

        <Field
          label="Title"
          value={form.title}
          onChange={(value) => update("title", value)}
          required
        />

        <Field
          label="Follow-up date & time"
          value={form.dueAt}
          onChange={(value) => update("dueAt", value)}
          type="datetime-local"
          required
        />

        <TextAreaField
          label="Notes"
          value={form.notes}
          onChange={(value) => update("notes", value)}
        />

        {error && <div className="cc360-write-error">{error}</div>}
        {saved && (
          <div className="cc360-write-success">
            Follow-up scheduled and refreshed.
          </div>
        )}

        <button id="cc360-follow-up-submit" type="submit" hidden />
      </form>
    </Modal>
  );
}

function Customer360OverviewPanels({
  overview,
  totalSpend,
  averageOrder,
  outstanding,
}: {
  overview: Customer360Overview;
  totalSpend?: number;
  averageOrder?: number;
  outstanding?: number;
}) {
  return (
    <>
      <Customer360Alerts alerts={overview.alerts} />

      <div className="cc360-summary-grid">
        {overview.summaryCards.map((card) => (
          <article className={`cc360-summary-card is-${card.state.toLowerCase()}`} key={card.type}>
            <div className="cc360-card-icon"><Icon name={summaryIcons[card.type]} size={18} /></div>
            <div>
              <span>{card.title}</span>
              <strong>{card.amountPaise != null ? fmt(card.amountPaise) : displayValue(card.value)}</strong>
              {card.subtitle && <small>{humanizeDisplayText(card.subtitle)}</small>}
              {card.timestamp && <small>{formatMaybeDate(card.timestamp)}</small>}
              <em>{sourceCopy[card.source]}</em>
            </div>
          </article>
        ))}
      </div>

      <div className="cc360-two-col">
        <Panel title="Operational State" icon="route">
          <InfoRow label="Recommended next step" value={overview.operationalState.recommendedNextAction.value ?? "No recommendation"} source={overview.operationalState.recommendedNextAction.source} />
          <InfoRow label="Open inquiry" value={overview.operationalState.openInquiry.value?.status ?? "None"} source={overview.operationalState.openInquiry.source} />
          <InfoRow label="Pending follow-up" value={overview.operationalState.pendingFollowUp.value?.title ?? "None"} source={overview.operationalState.pendingFollowUp.source} />
          <InfoRow label="Next occasion" value={overview.operationalState.nextOccasion.value ? `${overview.operationalState.nextOccasion.value.label} · ${formatMaybeDate(overview.operationalState.nextOccasion.value.date)}` : "Not captured"} source={overview.operationalState.nextOccasion.source} />
          <InfoRow label="Outstanding" value={overview.operationalState.outstandingAmount.value ? `${fmt(overview.operationalState.outstandingAmount.value.amountPaise)}${overview.operationalState.outstandingAmount.value.overdue ? " · overdue" : ""}` : "None"} source={overview.operationalState.outstandingAmount.source} />
        </Panel>

        <Panel title="Preferences" icon="cake-slice">
          <InfoRow label="Favorite flavors" value={displayList(overview.preferences.favoriteFlavors.value)} source={overview.preferences.favoriteFlavors.source} />
          <InfoRow label="Favorite occasions" value={displayList(overview.preferences.favoriteOccasions.value)} source={overview.preferences.favoriteOccasions.source} />
          <InfoRow label="Preferred delivery" value={overview.preferences.preferredDeliveryTime.value ?? "Not captured"} source={overview.preferences.preferredDeliveryTime.source} />
          <InfoRow label="Dietary preference" value={overview.preferences.dietaryPreference.value ?? "Not captured"} source={overview.preferences.dietaryPreference.source} />
          <InfoRow label="Allergy note" value={overview.preferences.allergyWarning.value ?? "Not captured"} source={overview.preferences.allergyWarning.source} urgent={!!overview.preferences.allergyWarning.value} />
        </Panel>
      </div>

      <div className="cc360-two-col">
        <Panel title="Customer Memory" icon="brain">
          {overview.memory.available ? (
            <>
              {(overview.memory.summaryLines.value ?? []).map((line) => <p className="cc360-memory-line" key={line}>{line}</p>)}
              <div className="cc360-tags">
                {(overview.memory.emotionalTags.value ?? []).map((tag) => <span className="cc360-soft-tag" key={tag}>{tag}</span>)}
              </div>
              <InfoRow label="Next predicted occasion" value={overview.memory.nextPredictedOccasion.value ?? "Unknown"} source={overview.memory.nextPredictedOccasion.source} />
              <InfoRow label="Churn risk" value={overview.memory.churnRiskLabel.value ?? "Unknown"} source={overview.memory.churnRiskLabel.source} />
              <InfoRow label="Repeat probability" value={overview.memory.repeatProbabilityLabel.value ?? "Unknown"} source={overview.memory.repeatProbabilityLabel.source} />
            </>
          ) : (
            <EmptyState icon="brain" title="Memory unavailable" description="This section is hidden or unavailable for the current role." />
          )}
        </Panel>

        <Panel title="Communication" icon="message-circle">
          <InfoRow label="State" value={overview.communication.state} source={overview.communication.source} />
          <InfoRow label="Assigned RM" value={overview.communication.assignedRm?.name ?? "Unassigned"} source={overview.communication.source} />
          <InfoRow label="Unread customer messages" value={overview.communication.unreadCount ?? 0} source={overview.communication.source} />
          <InfoRow label="Last customer message" value={formatMaybeDate(overview.communication.lastCustomerMessageAt)} source={overview.communication.source} />
          <InfoRow label="Last human reply" value={formatMaybeDate(overview.communication.lastHumanReplyAt)} source={overview.communication.source} />
          {overview.communication.lastMessagePreview && <p className="cc360-preview">"{overview.communication.lastMessagePreview}"</p>}
        </Panel>
      </div>

      <div className="cc360-two-col">
        <Panel title="Metrics" icon="gauge">
          <InfoRow label="Total orders" value={overview.metrics.totalOrders.value} source={overview.metrics.totalOrders.source} />
          <InfoRow label="Delivered orders" value={overview.metrics.deliveredOrders.value} source={overview.metrics.deliveredOrders.source} />
          <InfoRow label="Cancelled orders" value={overview.metrics.cancelledOrders.value} source={overview.metrics.cancelledOrders.source} />
          <InfoRow label="Lost inquiries" value={overview.metrics.lostInquiries.value} source={overview.metrics.lostInquiries.source} />
          {totalSpend != null && <InfoRow label="Total spend" value={fmt(totalSpend)} source={overview.metrics.totalSpendPaise?.source ?? "ORDER_DERIVED"} />}
          {averageOrder != null && <InfoRow label="Average order" value={fmt(averageOrder)} source={overview.metrics.averageOrderValuePaise?.source ?? "ORDER_DERIVED"} />}
          {outstanding != null && <InfoRow label="Outstanding balance" value={fmt(outstanding)} source={overview.metrics.outstandingPaise?.source ?? "PAYMENT_DERIVED"} />}
        </Panel>

        <Panel title="Data Completeness" icon="shield-check">
          <div className="cc360-score">
            <strong>{overview.dataCompleteness.score}%</strong>
            <span>Profile completeness</span>
          </div>
          <InfoRow label="Missing fields" value={displayList(overview.dataCompleteness.roleSafeMissingFields ?? overview.dataCompleteness.missingFields)} source={overview.dataCompleteness.source} />
          <InfoRow label="Recommended capture" value={displayList(overview.dataCompleteness.recommendedCapture)} source={overview.dataCompleteness.source} />
          <InfoRow label="Generated" value={formatMaybeDate(overview.metadata.generatedAt)} source="SYSTEM_INFERRED" />
        </Panel>
      </div>
    </>
  );
}

function Customer360ActivityPanel({ customerId }: { customerId: string }) {
  const [category, setCategory] = useState<Customer360ActivityCategory | "">("");
  const [type, setType] = useState<Customer360ActivityType | "">("");
  const activity = useCustomer360Activity(customerId, {
    category: category || undefined,
    type: type || undefined,
  });
  const items = activity.data?.pages.flatMap((page) => page.items) ?? [];
  const restricted = activity.data?.pages[0]?.metadata.sourcesRestricted ?? [];

  return (
    <Panel title="Unified Activity Timeline" icon="clock">
      <div className="cc360-activity-toolbar">
        <label>
          <span>Category</span>
          <select value={category} onChange={(event) => setCategory(event.target.value as Customer360ActivityCategory | "")}>
            <option value="">All categories</option>
            {CUSTOMER_360_ACTIVITY_CATEGORIES.map((item) => <option key={item} value={item}>{activityCategoryLabels[item]}</option>)}
          </select>
        </label>
        <label>
          <span>Type</span>
          <select value={type} onChange={(event) => setType(event.target.value as Customer360ActivityType | "")}>
            <option value="">All activity types</option>
            {CUSTOMER_360_ACTIVITY_TYPES.map((item) => <option key={item} value={item}>{item.replace(/_/g, " ")}</option>)}
          </select>
        </label>
      </div>

      {activity.isLoading && <LoadingState rows={4} />}
      {activity.isError && <ErrorState title="Could not load activity" description={getErr(activity.error)} />}
      {!activity.isLoading && !activity.isError && !items.length && <EmptyState icon="clock" title="No activity visible" description="No role-visible customer activity exists for this filter yet." />}

      {!!items.length && (
        <div className="cc360-activity-list">
          {items.map((item) => <Customer360ActivityRow item={item} key={item.id} />)}
        </div>
      )}

      {!!restricted.length && (
        <p className="cc360-activity-restricted">Some source families are restricted for this role: {restricted.join(", ")}.</p>
      )}

      {activity.hasNextPage && (
        <ActionButton
          icon="chevron-right"
          label={activity.isFetchingNextPage ? "Loading..." : "Load more"}
          onClick={() => activity.fetchNextPage()}
          disabled={activity.isFetchingNextPage}
        />
      )}
    </Panel>
  );
}

export function Customer360ActivityRow({ item }: { item: Customer360ActivityItem }) {
  return (
    <article className={`cc360-activity-item is-${item.category.toLowerCase()}`}>
      <div className="cc360-activity-icon"><Icon name={activityIcons[item.category] ?? "clock"} size={16} /></div>
      <div>
        <div className="cc360-activity-head">
          <strong>{item.title}</strong>
          <time dateTime={item.occurredAt}>{formatMaybeDate(item.occurredAt)}</time>
        </div>
        {item.summary && <p>{humanizeDisplayText(item.summary)}</p>}
        <div className="cc360-activity-meta">
          <span>{activityCategoryLabels[item.category]}</span>
          <span>{item.actor.name}</span>
          <span>{sourceCopy[item.source.label]}</span>
          {item.status && humanizeDisplayLabel(item.status, { omitUnknown: true }) && <span>{humanizeDisplayLabel(item.status, { omitUnknown: true })}</span>}
          {item.amountPaise != null && <span>{fmt(item.amountPaise)}</span>}
        </div>
      </div>
    </article>
  );
}

export function Customer360WriteModal({
  action,
  customer,
  role,
  relationshipOwner,
  onClose,
}: {
  action: Customer360QuickActionId;
  customer: Customer360Overview["customer"];
  role?: Customer360Overview["metadata"]["role"];
  relationshipOwner: string;
  onClose: () => void;
}) {
  const writeMutation = useCustomer360WriteMutation(customer.id);
  const assignableUsers = useWhatsAppAssignableUsers(action === "ASSIGN_RM");
  const initialForm = useMemo(() => initialWriteForm(action, customer, relationshipOwner), [action, customer, relationshipOwner]);
  const [form, setForm] = useState<Record<string, any>>(initialForm);
  const [error, setError] = useState("");
  const [saved, setSaved] = useState(false);
  const users = Array.isArray(assignableUsers.data) ? assignableUsers.data : [];

  useEffect(() => {
    setForm(initialForm);
    setError("");
    setSaved(false);
  }, [initialForm]);

  if (!customer360WriteActions.has(action)) return null;

  const config = writeConfig[action];
  const update = (key: string, value: any) => setForm((current) => ({ ...current, [key]: value }));
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    setError("");
    setSaved(false);
    try {
      await writeMutation.mutateAsync({ action, payload: normalizeWritePayload(action, form) });
      setSaved(true);
      setTimeout(onClose, 450);
    } catch (err) {
      setError(getErr(err));
    }
  };

  return (
    <Modal
      open
      title={config.title}
      onClose={onClose}
      footer={
        <>
          <ActionButton icon="x" label="Cancel" onClick={onClose} />
          <ActionButton icon="check" label={writeMutation.isPending ? "Saving..." : "Save"} onClick={() => document.getElementById("cc360-write-submit")?.click()} tone="primary" disabled={writeMutation.isPending} />
        </>
      }
    >
      <form className="cc360-write-form" onSubmit={submit}>
        <p>{config.description}</p>
        {action === "EDIT_CUSTOMER" && (
          <>
            <Field label="Name" value={form.name} onChange={(value) => update("name", value)} required />
            <Field label="Phone" value={form.phone} onChange={(value) => update("phone", value)} />
            <Field label="Email" value={form.email} onChange={(value) => update("email", value)} type="email" />
          </>
        )}
        {action === "ADD_ADDRESS" && (
          <>
            <Field label="Label" value={form.label} onChange={(value) => update("label", value)} placeholder="Home, Office, Venue" required />
            <Field label="Recipient" value={form.recipientName} onChange={(value) => update("recipientName", value)} required />
            <Field label="Phone" value={form.phone} onChange={(value) => update("phone", value)} required />
            <Field label="Address line" value={form.addressLine1} onChange={(value) => update("addressLine1", value)} required />
            <div className="cc360-write-grid">
              <Field label="City" value={form.city} onChange={(value) => update("city", value)} required />
              <Field label="State" value={form.state} onChange={(value) => update("state", value)} required />
              <Field label="Postal code" value={form.postalCode} onChange={(value) => update("postalCode", value)} required />
            </div>
            <CheckField label="Primary address" checked={Boolean(form.isPrimary)} onChange={(value) => update("isPrimary", value)} />
          </>
        )}
        {action === "RECORD_OCCASION" && (
          <>
            <SelectField label="Occasion type" value={form.type} onChange={(value) => update("type", value)} options={["BIRTHDAY", "ANNIVERSARY", "BUSINESS_EVENT", "FESTIVAL", "CUSTOM"]} />
            <Field label="Label" value={form.label} onChange={(value) => update("label", value)} placeholder="Daughter birthday" required />
            <Field label="Person" value={form.personName} onChange={(value) => update("personName", value)} />
            <div className="cc360-write-grid">
              <Field label="Month" value={form.month} onChange={(value) => update("month", value)} type="number" required />
              <Field label="Day" value={form.day} onChange={(value) => update("day", value)} type="number" required />
              <Field label="Year" value={form.year} onChange={(value) => update("year", value)} type="number" />
            </div>
            <CheckField label="Verified" checked={Boolean(form.isVerified)} onChange={(value) => update("isVerified", value)} />
          </>
        )}
        {action === "ADD_PREFERENCE" && (
          <>
            <SelectField label="Category" value={form.category} onChange={(value) => update("category", value)} options={["FLAVOR", "CAKE_TYPE", "EGG_PREFERENCE", "DESIGN_STYLE", "COLOR", "THEME", "BUDGET_RANGE", "DELIVERY_TIME", "PACKAGING", "COMMUNICATION_CHANNEL", "DIETARY", "ALLERGY", "DISLIKE", "OTHER"]} />
            <Field label="Value" value={form.value} onChange={(value) => update("value", value)} placeholder="Belgian chocolate" required />
            <SelectField label="Sentiment" value={form.sentiment} onChange={(value) => update("sentiment", value)} options={["POSITIVE", "NEUTRAL", "NEGATIVE"]} />
            <Field label="Notes" value={form.notes} onChange={(value) => update("notes", value)} />
            <CheckField label="Verified" checked={Boolean(form.isVerified)} onChange={(value) => update("isVerified", value)} />
          </>
        )}
        {action === "ADD_NOTE" && (
          <>
            <TextAreaField label="Note" value={form.body} onChange={(value) => update("body", value)} required />
            <SelectField label="Category" value={form.category} onChange={(value) => update("category", value)} options={noteCategoryOptions(role)} />
            <SelectField label="Visibility" value={form.visibility} onChange={(value) => update("visibility", value)} options={noteVisibilityOptions(role)} />
            <CheckField label="Pin note" checked={Boolean(form.isPinned)} onChange={(value) => update("isPinned", value)} />
          </>
        )}
        {action === "ASSIGN_RM" && (
          <>
            {users.length ? (
              <SelectField label="Relationship manager" value={form.assignedUserId} onChange={(value) => update("assignedUserId", value)} options={users.map((user: any) => ({ label: `${user.name} · ${user.role}`, value: user.id }))} />
            ) : (
              <Field label="Relationship manager user ID" value={form.assignedUserId} onChange={(value) => update("assignedUserId", value)} required />
            )}
            <Field label="Reason" value={form.reason} onChange={(value) => update("reason", value)} placeholder="Owns premium wedding accounts" />
          </>
        )}
        {error && <div className="cc360-write-error">{error}</div>}
        {saved && <div className="cc360-write-success">Saved and refreshed.</div>}
        <button id="cc360-write-submit" type="submit" hidden />
      </form>
    </Modal>
  );
}

function Field({ label, value, onChange, type = "text", placeholder, required = false }: { label: string; value?: string | number; onChange: (value: string) => void; type?: string; placeholder?: string; required?: boolean }) {
  return (
    <label className="cc360-write-field">
      <span>{label}</span>
      <input value={value ?? ""} onChange={(event) => onChange(event.target.value)} type={type} placeholder={placeholder} required={required} />
    </label>
  );
}

function TextAreaField({ label, value, onChange, required = false }: { label: string; value?: string; onChange: (value: string) => void; required?: boolean }) {
  return (
    <label className="cc360-write-field">
      <span>{label}</span>
      <textarea value={value ?? ""} onChange={(event) => onChange(event.target.value)} rows={4} required={required} />
    </label>
  );
}

function SelectField({ label, value, onChange, options }: { label: string; value?: string; onChange: (value: string) => void; options: Array<string | { label: string; value: string }> }) {
  return (
    <label className="cc360-write-field">
      <span>{label}</span>
      <select value={value ?? ""} onChange={(event) => onChange(event.target.value)}>
        <option value="" disabled>Select...</option>
        {options.map((option) => {
          const item = typeof option === "string" ? { label: option.replace(/_/g, " "), value: option } : option;
          return <option key={item.value} value={item.value}>{item.label}</option>;
        })}
      </select>
    </label>
  );
}

function CheckField({ label, checked, onChange }: { label: string; checked: boolean; onChange: (value: boolean) => void }) {
  return (
    <label className="cc360-write-check">
      <input type="checkbox" checked={checked} onChange={(event) => onChange(event.target.checked)} />
      <span>{label}</span>
    </label>
  );
}

const writeConfig: Record<string, { title: string; description: string }> = {
  EDIT_CUSTOMER: { title: "Edit customer identity", description: "Correct only identity fields you know are wrong." },
  ADD_ADDRESS: { title: "Add customer address", description: "Capture one delivery-ready address with provenance." },
  RECORD_OCCASION: { title: "Record occasion", description: "Add one verified or staff-entered date that matters." },
  ADD_PREFERENCE: { title: "Add preference", description: "Capture one reusable preference, allergy, dislike, or delivery habit." },
  ADD_NOTE: { title: "Add internal note", description: "Write a concise internal note. It is audited and role-filtered." },
  ASSIGN_RM: { title: "Assign relationship manager", description: "Set the permanent customer relationship owner." },
};

function initialWriteForm(action: Customer360QuickActionId, customer: Customer360Overview["customer"], relationshipOwner: string) {
  if (action === "EDIT_CUSTOMER") return { name: customer.displayName, phone: customer.primaryPhone ?? "", email: customer.email ?? "" };
  if (action === "ADD_ADDRESS") return { label: "Home", recipientName: customer.displayName, phone: customer.primaryPhone ?? "", addressLine1: "", city: "", state: "", postalCode: "", isPrimary: true };
  if (action === "RECORD_OCCASION") return { type: "BIRTHDAY", label: "", personName: "", month: "", day: "", year: "", isVerified: false };
  if (action === "ADD_PREFERENCE") return { category: "FLAVOR", value: "", sentiment: "POSITIVE", notes: "", isVerified: false };
  if (action === "ADD_NOTE") return { body: "", category: "GENERAL", visibility: "CUSTOMER_TEAM", isPinned: false };
  if (action === "ASSIGN_RM") return { assignedUserId: relationshipOwner, reason: "" };
  return {};
}

function normalizeWritePayload(action: Customer360QuickActionId, form: Record<string, any>) {
  if (action === "RECORD_OCCASION") {
    return { ...form, month: Number(form.month), day: Number(form.day), year: form.year ? Number(form.year) : undefined };
  }
  return form;
}

function noteCategoryOptions(role?: string) {
  return isOwnerAdminRole(role) ? ["GENERAL", "SALES", "SERVICE", "OWNER_PRIVATE"] : ["GENERAL", "SALES", "SERVICE"];
}

function noteVisibilityOptions(role?: string) {
  if (isOwnerAdminRole(role)) return ["CUSTOMER_TEAM", "MANAGERS_ONLY", "OWNER_ADMIN_ONLY"];
  if (isManagerRole(role)) return ["CUSTOMER_TEAM", "MANAGERS_ONLY"];
  return ["CUSTOMER_TEAM"];
}

function isOwnerAdminRole(role?: string) {
  return ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN"].includes(role ?? "");
}

function isManagerRole(role?: string) {
  return ["OWNER", "MASTER_ADMIN", "SUPER_ADMIN", "OPERATIONS_MANAGER", "SALES_MANAGER"].includes(role ?? "");
}

function Customer360QuickActions({ actions, onAction }: { actions: Customer360QuickAction[]; onAction: (action: Customer360QuickAction) => void }) {
  return (
    <div className="cc360-actions" aria-label="Customer actions">
      {actions.map((action) => (
        <button
          className="cc360-action"
          disabled={!action.enabled}
          key={action.id}
          onClick={() => onAction(action)}
          title={action.enabled ? action.label : action.disabledReason}
          type="button"
        >
          <Icon name={quickActionIcons[action.id] ?? "chevron-right"} size={17} />
          <span>{action.label}</span>
        </button>
      ))}
    </div>
  );
}

function Customer360Alerts({ alerts }: { alerts: Customer360Alert[] }) {
  if (!alerts.length) return null;
  return (
    <div className="cc360-alerts" aria-label="Customer alerts">
      {alerts.map((alert) => (
        <article className="cc360-alert" key={alert.id} style={{ borderColor: severityTone[alert.severity] }}>
          <div style={{ color: severityTone[alert.severity] }}><Icon name="triangle-alert" size={17} /></div>
          <div>
            <strong>{alert.title}</strong>
            <p>{humanizeDisplayText(alert.summary)}</p>
            <small>{sourceCopy[alert.source]}{alert.dueAt ? ` · Due ${formatMaybeDate(alert.dueAt)}` : ""}</small>
          </div>
        </article>
      ))}
    </div>
  );
}

function Panel({ title, icon, children }: { title: string; icon: IconName; children: ReactNode }) {
  return (
    <section className="cc360-panel">
      <div className="cc360-panel-title"><Icon name={icon} size={18} /><h3>{title}</h3></div>
      <div className="cc360-panel-body">{children}</div>
    </section>
  );
}

function InfoRow({ label, value, source, urgent = false }: { label: string; value: ReactNode; source: Customer360SourceLabel; urgent?: boolean }) {
  return (
    <div className={`cc360-info-row${urgent ? " is-urgent" : ""}`}>
      <span>{label}</span>
      <strong>{typeof value === "string" ? humanizeDisplayText(value) : value}</strong>
      <small>{sourceCopy[source]}</small>
    </div>
  );
}

function Customer360Loading() {
  return (
    <div className="cc360-placeholder">
      <LoadingState rows={6} />
    </div>
  );
}

function Customer360Error({ error }: { error: unknown }) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  const title = status === 403 ? "Customer access restricted" : status === 404 ? "Customer not found" : "Could not load customer overview";
  const description = status === 403 ? "Your role does not have access to Customer 360." : getErr(error);
  return (
    <div className="cc360-placeholder">
      <ErrorState title={title} description={description} />
    </div>
  );
}

export const CustPage = () => {
  const [q, setQ] = useState("");
  const [selId, setSelId] = useState("");
  const { data: custs = [], isFetching: customersFetching } = useCustSrch(q);
  const customer360 = useCustomer360(selId);
  const results = custs as CustomerSearchResult[];

  return (
    <div>
      <PageHeader
        eyebrow="CRM command center"
        title="Customers"
        subtitle="Search, select, and work from a governed Customer 360 overview. The detail panel only shows information allowed for your role."
      />
      <div className="cc360-layout">
        <aside className={`cc360-search-panel${selId ? " cc360-detail-open" : ""}`} aria-label="Customer search">
          <div className="cc360-search-box">
            <SearchInput value={q} onChange={setQ} placeholder="Search by name or phone, min 3 chars..." />
          </div>
          {q.length < 3 && <EmptyState icon="search" title="Search customers" description="Enter at least three characters to open a customer overview." />}
          {q.length >= 3 && customersFetching && <LoadingState rows={3} />}
          {q.length >= 3 && !customersFetching && !results.length && <EmptyState icon="user" title="No customers found" description="Try another phone or name, or create a new inquiry from Sales." />}
          <div className="cc360-result-list">
            {results.map((customer) => (
              <button
                className={`cc360-result${customer.id === selId ? " is-selected" : ""}`}
                key={customer.id}
                onClick={() => setSelId(customer.id)}
                type="button"
              >
                <span className="cc360-result-avatar">{customer.name?.slice(0, 1).toUpperCase() ?? "C"}</span>
                <span>
                  <strong>{customer.name ?? "Unnamed customer"}</strong>
                  <small>{customer.phone ?? "No phone"} · {customer.totalOrderCount ?? 0} orders · {fmt(customer.totalSpendPaise ?? 0)}</small>
                </span>
                {customer.profile?.highValueFlag && <em>VIP</em>}
              </button>
            ))}
          </div>
        </aside>

        <main className="cc360-main-panel">
          {!selId && (
            <div className="cc360-placeholder">
              <EmptyState icon="users" title="Select a customer" description="Customer 360 will load here after selection." />
            </div>
          )}
          {selId && customer360.isLoading && <Customer360Loading />}
          {selId && customer360.isError && <Customer360Error error={customer360.error} />}
          {selId && customer360.data && (
            <Customer360View
              overview={customer360.data}
              onBack={() => setSelId("")}
              onRefresh={() => customer360.refetch()}
            />
          )}
        </main>
      </div>
      <Customer360Styles />
    </div>
  );
};

function Customer360Styles() {
  return (
    <style>{`
      .cc360-layout { display: grid; grid-template-columns: minmax(280px, 360px) minmax(0, 1fr); gap: 18px; align-items: start; }
      .cc360-search-panel, .cc360-main-panel, .cc360-panel, .cc360-placeholder, .cc360-hero, .cc360-summary-card, .cc360-alert { background: ${creamTokens.color.surface}; border: 1px solid ${creamTokens.color.line}; border-radius: ${creamTokens.radius.card}px; box-shadow: ${creamTokens.shadow.card}; }
      .cc360-search-panel { padding: 16px; position: sticky; top: 92px; }
      .cc360-search-box { margin-bottom: 14px; }
      .cc360-result-list { display: grid; gap: 10px; }
      .cc360-result { width: 100%; border: 1px solid ${creamTokens.color.line}; background: ${creamTokens.color.cream}; border-radius: ${creamTokens.radius.medium}px; padding: 12px; display: grid; grid-template-columns: 42px 1fr auto; gap: 10px; align-items: center; text-align: left; cursor: pointer; color: ${creamTokens.color.ink}; }
      .cc360-result:hover, .cc360-result.is-selected { border-color: ${creamTokens.color.rose}; background: ${creamTokens.color.roseMist}; }
      .cc360-result-avatar, .cc360-avatar { display: inline-flex; align-items: center; justify-content: center; border-radius: 50%; background: linear-gradient(135deg, ${creamTokens.color.rose}, ${creamTokens.color.chocolate}); color: white; font-weight: 900; }
      .cc360-result-avatar { width: 42px; height: 42px; }
      .cc360-result strong { display: block; font-size: 14px; }
      .cc360-result small { color: ${creamTokens.color.muted}; font-size: 12px; }
      .cc360-result em { font-style: normal; font-size: 11px; font-weight: 900; color: ${creamTokens.color.roseDeep}; }
      .cc360-main-panel { min-width: 0; }
      .cc360-detail { display: grid; gap: 16px; padding-bottom: clamp(104px, 12vh, 152px); }
      .cc360-mobile-back { display: none; }
      .cc360-hero { padding: 20px; display: grid; grid-template-columns: 72px minmax(0, 1fr) minmax(190px, 240px); gap: 16px; align-items: center; }
      .cc360-avatar { width: 72px; height: 72px; font-size: 30px; }
      .cc360-eyebrow { font-size: 11px; font-weight: 900; text-transform: uppercase; color: ${creamTokens.color.roseDeep}; }
      .cc360-hero h2 { margin: 4px 0 8px; font-size: 30px; line-height: 1.05; color: ${creamTokens.color.chocolate}; }
      .cc360-contact, .cc360-tags, .cc360-actions { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; }
      .cc360-contact span { color: ${creamTokens.color.muted}; font-size: 13px; }
      .cc360-rm-card { border: 1px solid ${creamTokens.color.line}; background: ${creamTokens.color.cream}; border-radius: ${creamTokens.radius.medium}px; padding: 14px; }
      .cc360-rm-card span, .cc360-rm-card small, .cc360-info-row small, .cc360-summary-card em, .cc360-alert small { display: block; color: ${creamTokens.color.muted}; font-size: 11px; font-style: normal; }
      .cc360-rm-card strong { display: block; margin: 3px 0; color: ${creamTokens.color.ink}; }
      .cc360-actions { background: ${creamTokens.color.cream}; border: 1px solid ${creamTokens.color.line}; border-radius: ${creamTokens.radius.card}px; padding: 12px; box-shadow: ${creamTokens.shadow.card}; }
      .cc360-action { border: 1px solid ${creamTokens.color.line}; border-radius: ${creamTokens.radius.control}px; background: white; color: ${creamTokens.color.ink}; padding: 10px 12px; font-weight: 800; display: inline-flex; gap: 8px; align-items: center; cursor: pointer; }
      .cc360-action:disabled { cursor: not-allowed; opacity: .48; }
      .cc360-tabs { display: inline-flex; gap: 6px; background: ${creamTokens.color.cream}; border: 1px solid ${creamTokens.color.line}; border-radius: ${creamTokens.radius.pill}px; padding: 5px; width: fit-content; }
      .cc360-tabs button { border: 0; border-radius: ${creamTokens.radius.pill}px; background: transparent; color: ${creamTokens.color.muted}; padding: 9px 14px; font-weight: 900; cursor: pointer; }
      .cc360-tabs button.is-active { background: ${creamTokens.color.surface}; color: ${creamTokens.color.roseDeep}; box-shadow: ${creamTokens.shadow.card}; }
      .cc360-alerts { display: grid; gap: 10px; }
      .cc360-alert { padding: 13px; display: grid; grid-template-columns: 24px 1fr; gap: 8px; border-left-width: 4px; }
      .cc360-alert strong { color: ${creamTokens.color.ink}; }
      .cc360-alert p { margin: 3px 0; color: ${creamTokens.color.muted}; font-size: 13px; }
      .cc360-summary-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 12px; }
      .cc360-summary-card { padding: 16px; display: grid; grid-template-columns: 34px 1fr; gap: 10px; min-width: 0; }
      .cc360-summary-card span { color: ${creamTokens.color.muted}; font-size: 12px; font-weight: 800; }
      .cc360-summary-card strong { display: block; margin: 4px 0; color: ${creamTokens.color.chocolate}; font-size: 20px; line-height: 1.1; word-break: break-word; }
      .cc360-summary-card small { display: block; color: ${creamTokens.color.muted}; font-size: 12px; }
      .cc360-card-icon { width: 34px; height: 34px; border-radius: ${creamTokens.radius.control}px; background: ${creamTokens.color.roseMist}; color: ${creamTokens.color.roseDeep}; display: inline-flex; align-items: center; justify-content: center; }
      .cc360-two-col { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 14px; }
      .cc360-panel { padding: 16px; }
      .cc360-panel-title { display: flex; gap: 8px; align-items: center; color: ${creamTokens.color.roseDeep}; margin-bottom: 12px; }
      .cc360-panel-title h3 { margin: 0; color: ${creamTokens.color.chocolate}; font-size: 16px; }
      .cc360-panel-body { display: grid; gap: 10px; }
      .cc360-info-row { border-bottom: 1px solid ${creamTokens.color.line}; padding-bottom: 9px; }
      .cc360-info-row:last-child { border-bottom: 0; padding-bottom: 0; }
      .cc360-info-row span { display: block; color: ${creamTokens.color.muted}; font-size: 12px; font-weight: 800; }
      .cc360-info-row strong { display: block; color: ${creamTokens.color.ink}; font-size: 14px; margin: 2px 0; }
      .cc360-info-row.is-urgent strong { color: ${creamTokens.color.critical}; }
      .cc360-memory-line, .cc360-preview { margin: 0; color: ${creamTokens.color.ink}; font-size: 13px; line-height: 1.55; }
      .cc360-preview { background: ${creamTokens.color.cream}; border-radius: ${creamTokens.radius.medium}px; padding: 12px; font-style: italic; }
      .cc360-activity-toolbar { display: grid; grid-template-columns: minmax(160px, 220px) minmax(200px, 1fr); gap: 10px; align-items: end; }
      .cc360-activity-toolbar label { display: grid; gap: 6px; }
      .cc360-activity-toolbar span { color: ${creamTokens.color.muted}; font-size: 12px; font-weight: 900; }
      .cc360-activity-toolbar select { width: 100%; border: 1.5px solid ${creamTokens.color.line}; background: ${creamTokens.color.surface}; color: ${creamTokens.color.ink}; border-radius: ${creamTokens.radius.control}px; padding: 10px 11px; font: inherit; outline: none; }
      .cc360-activity-list { display: grid; gap: 10px; }
      .cc360-activity-item { display: grid; grid-template-columns: 38px minmax(0, 1fr); gap: 12px; border: 1px solid ${creamTokens.color.line}; border-radius: ${creamTokens.radius.medium}px; background: ${creamTokens.color.cream}; padding: 12px; }
      .cc360-activity-icon { width: 38px; height: 38px; border-radius: ${creamTokens.radius.control}px; display: inline-flex; align-items: center; justify-content: center; background: ${creamTokens.color.roseMist}; color: ${creamTokens.color.roseDeep}; }
      .cc360-activity-head { display: flex; justify-content: space-between; gap: 12px; align-items: baseline; }
      .cc360-activity-head strong { color: ${creamTokens.color.ink}; font-size: 14px; }
      .cc360-activity-head time, .cc360-activity-meta, .cc360-activity-restricted { color: ${creamTokens.color.muted}; font-size: 12px; }
      .cc360-activity-item p { margin: 4px 0 7px; color: ${creamTokens.color.ink}; font-size: 13px; line-height: 1.45; }
      .cc360-activity-meta { display: flex; flex-wrap: wrap; gap: 7px; }
      .cc360-activity-meta span { border: 1px solid ${creamTokens.color.line}; background: white; border-radius: ${creamTokens.radius.pill}px; padding: 4px 8px; font-weight: 800; }
      .cc360-activity-restricted { margin: 0; }
      .cc360-soft-tag { background: ${creamTokens.color.roseMist}; color: ${creamTokens.color.roseDeep}; border-radius: ${creamTokens.radius.pill}px; padding: 5px 9px; font-size: 12px; font-weight: 800; }
      .cc360-score { background: linear-gradient(135deg, ${creamTokens.color.roseMist}, white); border: 1px solid ${creamTokens.color.line}; border-radius: ${creamTokens.radius.medium}px; padding: 14px; }
      .cc360-score strong { display: block; font-size: 30px; color: ${creamTokens.color.chocolate}; }
      .cc360-score span { color: ${creamTokens.color.muted}; font-size: 12px; font-weight: 800; }
      .cc360-placeholder { padding: 22px; }
      .cc360-write-form { display: grid; gap: 13px; }
      .cc360-write-form p { margin: 0; color: ${creamTokens.color.muted}; font-size: 13px; line-height: 1.55; }
      .cc360-write-grid { display: grid; grid-template-columns: repeat(3, minmax(0, 1fr)); gap: 10px; }
      .cc360-write-field { display: grid; gap: 6px; }
      .cc360-write-field span, .cc360-write-check span { color: ${creamTokens.color.muted}; font-size: 12px; font-weight: 900; }
      .cc360-write-field input, .cc360-write-field select, .cc360-write-field textarea { width: 100%; box-sizing: border-box; border: 1.5px solid ${creamTokens.color.line}; background: ${creamTokens.color.surface}; color: ${creamTokens.color.ink}; border-radius: ${creamTokens.radius.control}px; padding: 10px 11px; font: inherit; outline: none; }
      .cc360-write-field input:focus, .cc360-write-field select:focus, .cc360-write-field textarea:focus { border-color: ${creamTokens.color.rose}; box-shadow: 0 0 0 3px ${creamTokens.color.roseMist}; }
      .cc360-write-check { display: flex; align-items: center; gap: 9px; }
      .cc360-write-check input { width: 18px; height: 18px; accent-color: ${creamTokens.color.rose}; }
      .cc360-write-error, .cc360-write-success { border-radius: ${creamTokens.radius.medium}px; padding: 10px 12px; font-size: 13px; font-weight: 800; }
      .cc360-write-error { color: ${creamTokens.color.critical}; background: #FDEAEA; border: 1px solid ${creamTokens.color.critical}33; }
      .cc360-write-success { color: ${creamTokens.color.success}; background: #E6F5EC; border: 1px solid ${creamTokens.color.success}33; }
      @media (max-width: 1120px) {
        .cc360-summary-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
        .cc360-hero { grid-template-columns: 60px 1fr; }
        .cc360-rm-card { grid-column: 1 / -1; }
      }
      @media (max-width: 860px) {
        .cc360-layout, .cc360-two-col { grid-template-columns: 1fr; }
        .cc360-search-panel { position: static; }
        .cc360-search-panel.cc360-detail-open { display: none; }
        .cc360-mobile-back { display: block; }
      }
      @media (max-width: 620px) {
        .cc360-summary-grid, .cc360-hero, .cc360-activity-toolbar { grid-template-columns: 1fr; }
        .cc360-activity-head { display: grid; gap: 4px; }
        .cc360-write-grid { grid-template-columns: 1fr; }
        .cc360-hero h2 { font-size: 24px; }
        .cc360-detail { padding-bottom: 168px; }
      }
    `}</style>
  );
}

export default CustPage;
